/** US full-game sports supported by the shared consumer and trading paths.
 * A sport in this list is not evidence of a feed, market, account or permission.
 * Props require their own canonical identity integration (#1030). Pure module:
 * the browser imports the same contract; do not add Node or transport imports. */
export const US_GAME_SPORTS = Object.freeze(['NFL', 'CFB', 'NBA', 'NHL'] as const);
export type UsGameSport = typeof US_GAME_SPORTS[number];
export type UsCanonicalSport = Lowercase<UsGameSport>;
export type UsTransportSport = UsGameSport | `${UsGameSport}ALT`;
export type UsPeriod = 'full' | '1H' | '2H' | '1Q' | '2Q' | '3Q' | '4Q';
export const US_TRANSPORT_SPORTS = Object.freeze(US_GAME_SPORTS.flatMap(s => [s, `${s}ALT` as UsTransportSport]));

/** Raw feed assignment and application sport are distinct identities. */
export function usBaseSport(value: unknown): UsGameSport | null {
  if (typeof value !== 'string') return null;
  const base = value.endsWith('ALT') ? value.slice(0, -3) : value;
  return isUsGameSport(base) ? base : null;
}

/** Only the documented CFB/ncaaf alias is normalized. No title/slug inference. */
export function normalizeUsGameKey(value: unknown, sport: UsGameSport): string | null {
  if (typeof value !== 'string') return null;
  const p = value.split('|');
  if (p.length !== 5 || !/^\d{4}-\d{2}-\d{2}$/.test(p[1]) || !/^\d+$/.test(p[2])
    || !/^[A-Z0-9]{1,8}$/.test(p[3]) || !/^[A-Z0-9]{1,8}$/.test(p[4]) || p[3] === p[4]
    || !(p[0] === sport.toLowerCase() || sport === 'CFB' && p[0] === 'ncaaf')) return null;
  p[0] = sport.toLowerCase(); return p.join('|');
}

/** Typed classification supplies the period; absent/unknown never means full. */
export function usPeriodForMarketType(value: string): UsPeriod | null {
  const periods = [['full_game', 'full'], ['first_half', '1H'], ['second_half', '2H'],
    ['first_quarter', '1Q'], ['second_quarter', '2Q'], ['third_quarter', '3Q'], ['fourth_quarter', '4Q']] as const;
  const found = periods.filter(([token]) => (`_${value}_`).includes(`_${token}_`));
  return found.length === 1 ? found[0][1] : null;
}

/** #1030: explicit binary football period-winner contracts. A prop label alone
 * does not identify an instrument; totals, player props, ties and events stay
 * outside this family. The venue marketType remains `props`. */
export function usPeriodWinner(value: string, sport: UsGameSport): Exclude<UsPeriod, 'full'> | null {
  if (sport !== 'NFL' && sport !== 'CFB'
    || !/^football_team_(first_half|second_half|first_quarter|second_quarter|third_quarter|fourth_quarter)_winner$/.test(value)) return null;
  const period = usPeriodForMarketType(value);
  return period && period !== 'full' ? period : null;
}

export function isUsGameSport(value: unknown): value is UsGameSport {
  return typeof value === 'string' && US_GAME_SPORTS.some(sport => sport === value);
}

/** Board spelling only; no market classification or title inference. */
export function usGameSportForBoard(value: string): UsGameSport | null {
  const sport = value.toUpperCase();
  if (sport === 'NCAAF') return 'CFB';
  return isUsGameSport(sport) ? sport : null;
}

/** Only a canonical key supplies a discovery sport. Unknown never means NFL. */
export function usGameSportForKey(key: string | null): UsGameSport | null {
  if (!key) return null;
  const parts = key.split('|');
  const sport = usGameSportForBoard(parts[0]);
  return sport && normalizeUsGameKey(key, sport) ? sport : null;
}
